import * as React from "react";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { FetchMediaMetrics } from "../../../api/general";
import { capitalizeInitial } from "../../../utils/text";
import { DateTime } from "luxon";
import styled from "styled-components";
import "/public/styles/color.css";
import "/public/styles/size.css";
import { auth } from "../../../firebase";
import { useAuthState } from "react-firebase-hooks/auth";
import {
  MetricStatus,
  TaggedMetric,
  meanCountMetric,
  maxCountMetric,
  speciesEvennessMetric,
  speciesDiversityMetric,
  speciesIdentifiedMetric,
  speciesRichnessMetric,
} from "../../../domain/metrics";

export const MediaDetailPage = (props: {}) => {
  const [theme, setTheme] = useState<string>("Light");
  const [metrics, setMetrics] = useState(null);
  const [taxonomy, setTaxonomy] = useState({
    1: {
      common_name: "animal",
      scientific_name: "animalia",
    },
    2: {
      common_name: "Indo-Pacific sergeant",
      scientific_name: "abudefduf vaigiensis",
    },
  });
  const [media, setMedia] = useState({});
  const [user, loading, error] = useAuthState(auth);
  let id: string;
  ({ id } = useParams());
  useEffect(() => {
    if (!user) {
      return;
    }
    FetchMediaMetrics(user, id).then((m) => {
      setMetrics(m);
    });
  }, [user, id]);
  return (
    <>
      <MediaMetadata media={media} />
      <MetricsDisplay
        metrics={metrics}
        media={media}
        taxonomy={taxonomy}
        now={DateTime.now()}
      />
    </>
  );
};

const MediaMetadata = ({ media }) => {
  return (
    <MediaMetadataContainer>
      <MediaAttribution>@dlunsford / taiwan</MediaAttribution>
      <MediaTitleBar>
        <MediaTitle>Taiwan_Survey_A001</MediaTitle>
      </MediaTitleBar>
      <MediaMetadataDetail>
        <ProjectAttribution>Managed by Orsted</ProjectAttribution>
        <MediaMetadataBlock>
          <MediaMetadataItem label="Video Duration" value="22hr 4m 5s" />
          <MediaMetadataItem label="Survey Date" value="22.04.2023" />
          <MediaMetadataItem label="Survey Time" value="12:20 GMT+2" />
          <MediaMetadataItem label="Location" value="Taipai" />
        </MediaMetadataBlock>
      </MediaMetadataDetail>
    </MediaMetadataContainer>
  );
};

const ProjectAttribution = styled.div`
  display: inline-block;
`;

const MediaMetadataBlock = styled.div`
  display: inline-block;
  /*position: absolute;*/
  text-align: centre;
`;

const MediaMetadataItem = ({ label, value }) => {
  return (
    <MediaMetadataItemContainer>
      <div>{label}</div>
      <MediaMetadataLosenge>{value}</MediaMetadataLosenge>
    </MediaMetadataItemContainer>
  );
};

const MediaMetadataItemContainer = styled.div`
  display: inline-block;
  padding: var(--space-100);
`;

const MediaMetadataContainer = styled.div`
  color: var(--colorContent-default);
`;

const MediaAttribution = styled.div``;

const MediaTitleBar = styled.div``;

const MediaTitle = styled.h3``;

const MediaMetadataDetail = styled.div`
  vertical-align: middle;
`;

const MediaMetadataLosenge = styled.div`
  padding: var(--space-025) var(--space-100);
  border-radius: var(--borderRadius-full);
  background-color: var(--colorBackground-neutral-muted);
  display: inline-block;
`;

const MetricsDisplay = ({ media, metrics, taxonomy, now }) => {
  return (
    <VerticalGrid>
      <HorizontalGrid>
        <VideoDisplay media={media} />
        <VerticalGrid>
          <ScalarMetric
            label="Mean Count"
            description="Mean count per frame"
            metric={meanCountMetric(metrics)}
            now={now}
          />
          <ScalarMetric
            label="Max Count"
            description="Max count per frame"
            metric={maxCountMetric(metrics)}
            now={now}
          />
        </VerticalGrid>
      </HorizontalGrid>
      <HorizontalGrid>
        <ArrayMetric
          label="Species identified"
          metric={speciesIdentifiedMetric(metrics, taxonomy)}
          now={now}
        />
        <ScalarMetric
          label="Species Evenness"
          description="Species evenness over entire video"
          metric={speciesEvennessMetric(metrics)}
          now={now}
        />
        <ScalarMetric
          label="Species Diversity"
          description="Species diversity over entire video"
          metric={speciesDiversityMetric(metrics)}
          now={now}
        />
        <ScalarMetric
          label="Species Richness"
          description="Species richness over entire video"
          metric={speciesRichnessMetric(metrics)}
          now={now}
        />
      </HorizontalGrid>
    </VerticalGrid>
  );
};

const VideoDisplay = ({ media }) => {
  return <VideoContainer>Video Placeholder</VideoContainer>;
};

const VideoContainer = styled.div`
  display: flex;
  width: 905px;
  height: 524px;
  align-items: center;
  justify-content: center;
  border: solid;
  border-radius: var(--border-radius-xlarge);
  border-color: var(--colorBorder-default);
  border-width: 1px;
  color: var(--colorBorder-default);
`;

// metric:
//   icon
//   name
//   description
//   value
//   age

const HorizontalGrid = styled.div`
  display: flex;
  flex-direction: row;
  gap: var(--space-300);
`;

const VerticalGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-300);
`;

const ScalarMetric = ({
  label,
  description,
  metric,
  now,
}: {
  label: string;
  description: string;
  metric: TaggedMetric;
  now: DateTime;
}) => {
  return (
    <MetricBox>
      <MetricHeader>
        <MetricLabel>
          <MetricIcon href="#icon-fish" />
          {label}
        </MetricLabel>
      </MetricHeader>
      <MetricResultOuter>
        <MetricResultWatermark />
        <MetricResultInner>
          <MetricDescription>{description}</MetricDescription>
          <MetricValue>{metric?.value || "No data"}</MetricValue>
        </MetricResultInner>
      </MetricResultOuter>
      <MetricFooter>
        <StatusBadge
          status={metric?.status || MetricStatus.Offline}
          updated={metric?.latestTime || DateTime.now()}
          now={now}
        />
      </MetricFooter>
    </MetricBox>
  );
};

const ArrayMetric = ({
  label,
  metric,
  now,
}: {
  label: string;
  metric: TaggedMetric<Array>;
  now: DateTime;
}) => {
  const rows = [];
  for (let item of metric ? metric.value : []) {
    rows.push(<div key={item}>{capitalizeInitial(item)}</div>);
  }
  return (
    <MetricBox>
      <MetricHeader>
        <MetricLabel>
          <MetricIcon href="#icon-fish" />
          {label}
        </MetricLabel>
      </MetricHeader>
      <MetricResultOuter>
        <MetricResultWatermark />
        <MetricResultInner>
          <MetricArrayValue>
            {rows.length > 0 ? rows : "No data"}
          </MetricArrayValue>
        </MetricResultInner>
      </MetricResultOuter>
      <MetricFooter>
        <StatusBadge
          status={metric?.status || MetricStatus.Offline}
          updated={metric?.latestTime || DateTime.now()}
          now={now}
        />
      </MetricFooter>
    </MetricBox>
  );
};

const MetricHeader = styled.div`
  font-size: 14px;
  line-height: 20px;
  letter: -1%;
  weight: 600;
  color: var(--colorContent-default);
`;

const MetricResultOuter = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  /*width: 100%;*/
  height: 100%;
  gap: 8px;
`;

const MetricResultInner = styled.div`
  padding-right: var(--space-100);
  padding-left: var(--space-100);
  gap: var(--space-100);
  z-index: 1;
`;

const MetricFooter = styled.div`
  gap: 10px;
  text-align: right;
`;

const MetricBox = styled.div`
  flex-direction: column;
  display: flex;
  width: 320px;
  height: 264px;
  padding-top: var(--space-100);
  padding-right: var(--space-150);
  padding-bottom: var(--space-100);
  padding-left: var(--space-150);
  border: solid;
  border-width: 1px;
  border-color: var(--colorBorder-default);
  border-radius: var(--border-radius-large);
`;

const MetricLabel = styled.div``;

const MetricDescription = styled.div`
  width: 100%;
  text-align: center;
  font-size: 16px;
  line-height: 24px;
  letter: -1%;
  color: var(--colorContent-neutral);
`;
const MetricValue = styled.div`
  width: 100%;
  text-align: center;
  font-size: 64px;
  line-height: 80px;
  letter: -2%;
  color: var(--colorContent-accent);
`;
const MetricArrayValue = styled.div`
  width: 100%;
  text-align: center;
  font-size: 14px;
  color: var(--colorContent-accent);
`;

const MetricResultWatermark = () => {
  return (
    <svg
      style={{ position: "absolute", fillOpacity: 0.22 }}
      width="144"
      height="144"
      version="2.0"
    >
      <use href="#logo-brandmark" />
    </svg>
  );
};

const StatusBadge = ({
  status,
  updated,
  now,
}: {
  status: MetricStatus;
  updated: DateTime;
  now: DateTime;
}) => {
  switch (status) {
    case MetricStatus.UpToDate:
      return <UpToDateBadge updated={updated} now={now} />;
    case MetricStatus.Outdated:
      return <OutdatedBadge updated={updated} now={now} />;
    case MetricStatus.Running:
      return <RunningBadge updated={updated} now={now} />;
    case MetricStatus.Offline:
      return <OfflineBadge updated={updated} now={now} />;
    case MetricStatus.New:
      return <NewBadge updated={updated} now={now} />;
    default:
      return <></>;
  }
};

const UpToDateBadge = ({
  updated,
  now,
}: {
  updated: DateTime;
  now: DateTime;
}) => {
  return (
    <StatusBlock className="status-up-to-date">
      <Age then={updated} now={now} />
      <BadgeIcon href="#patch-check" />
    </StatusBlock>
  );
};

const OutdatedBadge = ({
  updated,
  now,
}: {
  updated: DateTime;
  now: DateTime;
}) => {
  return (
    <StatusBlock className="status-outdated">
      <Age then={updated} now={now} />
      <BadgeIcon href="#exclamation-triangle" />
    </StatusBlock>
  );
};

const RunningBadge = ({
  updated,
  now,
}: {
  updated: DateTime;
  now: DateTime;
}) => {
  return (
    <StatusBlock className="status-running">
      Running
      <BadgeIcon href="#soundwave" />
    </StatusBlock>
  );
};

const OfflineBadge = ({
  updated,
  now,
}: {
  updated: DateTime;
  now: DateTime;
}) => {
  return (
    <StatusBlock className="status-offline">
      Offline
      <BadgeIcon href="#x-octagon" />
    </StatusBlock>
  );
};

const NewBadge = ({ updated, now }: { updated: DateTime; now: DateTime }) => {
  return (
    <StatusBlock className="status-new">
      New
      <BadgeIcon href="#info-circle" />
    </StatusBlock>
  );
};

const StatusBlock = styled.div`
  display: inline-block;
  padding-right: var(--space-075);
  padding-left: var(--space-075);
  border-radius: var(--border-radius-xsmall);
  gap: var(--space-050);
`;

export const Age = ({ then, now }: { then: DateTime; now: DateTime }) => {
  let { val, units } = approximateDuration(now.diff(then));
  return (
    <>
      {val}
      {shortTimeUnits(units)}
    </>
  );
};

const shortTimeUnits = (units: string): string => {
  switch (units) {
    case "seconds":
      return "sec";
    case "minutes":
      return "min";
    case "hours":
      return "h";
    case "days":
      return "d";
    case "weeks":
      return "w";
    case "months":
      return "mo";
    case "years":
      return "y";
    default:
      return units;
  }
};

const approximateDuration = (
  duration: Duration,
): { val: number; units: string } => {
  const units_ = [
    "years",
    "months",
    "weeks",
    "days",
    "hours",
    "minutes",
    "seconds",
  ];
  for (let units of units_) {
    if (duration.as(units) >= 1.0) {
      return { val: Math.trunc(duration.as(units)), units: units };
    }
  }
  return { val: Math.ceil(duration.as("seconds")), units: "seconds" };
};

const BadgeIcon = ({ href }: { href: string }) => {
  return (
    <svg width="12" height="12" version="2.0">
      <use href={href} />
    </svg>
  );
};

const MetricIcon = ({ href }: { href: string }) => {
  return (
    <svg width="16" height="10" version="2.0">
      <use href={href} />
    </svg>
  );
};
